/**
 * AiChatRuntime — creates the assistant-ui runtime and wraps chat components.
 *
 * This component is keyed by `chatSessionKey` from the provider, so when
 * the user switches threads or creates a new chat, only this subtree
 * remounts — giving a fresh runtime without touching the sidebar or app layout.
 *
 * Thread persistence is owned entirely here:
 *   - First exchange → atomically creates a Firestore thread with messages
 *   - Subsequent exchanges → updates the existing thread's messages
 *   - ephemeral mode (panel) → skips all persistence
 */
import {
  type ReactNode,
  type ErrorInfo,
  Component,
  useRef,
  useMemo,
  useState,
  useEffect,
  useCallback,
  useLayoutEffect,
} from 'react';
import { lastAssistantMessageIsCompleteWithToolCalls, type UIMessage } from 'ai';
import { useChat } from '@ai-sdk/react';
import {
  AssistantRuntimeProvider,
  useAuiState,
  unstable_useRemoteThreadListRuntime,
  unstable_useCloudThreadListAdapter,
} from '@assistant-ui/react';
import { useAISDKRuntime, AssistantChatTransport } from '@assistant-ui/react-ai-sdk';
import { useAuth } from '@/contexts/AuthContext';
import { api, ApiRequestError } from '@/services/api';
import { toast } from '@/components/ui/Toast';
import { StorageAttachmentAdapter } from './adapters/storage-attachment-adapter';
import { threadApi, knownMessagesVersion, noteMessagesVersion, type ActiveTurn } from './adapters/firestore-thread-adapter';
import { useSectionContext, useThreadState, useModelSelection } from './AiChatProvider';
import { stampAbortedTurn } from './turnTiming';
import {
  TaskEventsContext,
  createTaskEventsStore,
  notifyWakeTurnDone,
  useTaskEventsConnection,
  type TaskEventsStore,
} from './BackgroundTasks';

// ── Stop ─────────────────────────────────────────────────────────────────────

/**
 * Tell the server to stop a turn, and say so when it could not be told.
 *
 * The turn is named by the id this client gave it at send time, which the server
 * records durably so the turn finds it on whichever instance it runs, even one
 * that has not finished starting. Retried because a Stop that never arrives
 * leaves an agent working with nothing on screen; the thread then looks idle,
 * so the one thing worse than failing is failing silently.
 */
async function requestTurnStop(turnId: string, threadId: string | null): Promise<void> {
  const body = { turnId, ...(threadId ? { threadId } : {}) };
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await api.post('/chat/abort', body);
      return;
    } catch {
      await new Promise((r) => setTimeout(r, 500 * 2 ** attempt));
    }
  }
  toast.error('Stop did not reach the server, so the agent may still be running. Press Stop again or reload before sending.');
}

// ── Error Boundary ───────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean;
}

class AiChatErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AiChat] Runtime error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full p-6 text-center">
          <div>
            <p className="text-sm font-medium text-zinc-700">Chat encountered an error</p>
            <p className="text-xs text-zinc-400 mt-1">This usually resolves by retrying.</p>
            <button
              onClick={() => this.setState({ hasError: false })}
              className="mt-3 px-4 py-2 text-xs font-medium rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function deriveTitleFromMessages(messages: UIMessage[]): string {
  const firstUserText = messages
    .find((m) => m.role === 'user')
    ?.parts?.find((p): p is { type: 'text'; text: string } => (p as any).type === 'text')
    ?.text ?? '';
  if (!firstUserText) return 'New Chat';
  return firstUserText.length > 60 ? firstUserText.slice(0, 57) + '...' : firstUserText;
}

// ── Thread Loader ───────────────────────────────────────────────────────────
// Fetches messages for an existing thread, then mounts the runtime component.
// This two-phase approach ensures useChatRuntime receives messages on its very
// first invocation — the hook creates the Chat object only once and ignores
// later changes to the messages option.

interface LoaderProps {
  children: ReactNode;
  ephemeral?: boolean;
}

function AiChatThreadLoader({ children, ephemeral }: LoaderProps) {
  const { currentThreadId } = useThreadState();
  const { user, loading: authLoading } = useAuth();
  const effectiveThreadId = ephemeral ? null : currentThreadId;

  const [initialMessages, setInitialMessages] = useState<UIMessage[] | undefined>(undefined);
  // A turn still running on the thread — a reload mid-turn reattaches to it.
  const [resumeTurn, setResumeTurn] = useState<ActiveTurn | null>(null);
  // Stay in the loading state until either (a) there's no thread to load, or
  // (b) auth is ready AND we've fetched the thread.  If we proceed before
  // auth is ready, `_getToken` returns null, the request goes out without a
  // Bearer header, the backend rejects with 401, and we render an empty chat
  // on every page refresh — which was the original bug.
  const [ready, setReady] = useState(!effectiveThreadId);

  useEffect(() => {
    if (!effectiveThreadId) {
      setInitialMessages(undefined);
      setReady(true);
      return;
    }

    // Wait until Firebase auth has resolved.  AuthContext sets the api
    // token provider in a useEffect after `user` updates, so we also need
    // a real user object before firing — otherwise we race the provider
    // wire-up and ship the request with no token.
    if (authLoading || !user) {
      setReady(false);
      return;
    }

    let cancelled = false;
    threadApi.get(effectiveThreadId).then((thread) => {
      if (cancelled) return;
      noteMessagesVersion(effectiveThreadId, thread.messagesVersion);
      let msgs = (thread.messages as UIMessage[]) ?? [];
      const turn = thread.activeTurn ?? null;
      // The running turn's prompt is not saved yet. Put it back before the
      // replayed reply, which would otherwise continue the previous answer.
      const prompt = turn?.userMessage as UIMessage | undefined;
      if (prompt?.id && !msgs.some((m) => m.id === prompt.id)) msgs = [...msgs, prompt];
      setInitialMessages(msgs.length > 0 ? msgs : undefined);
      setResumeTurn(turn);
      setReady(true);
    }).catch((err) => {
      console.warn('[AiChat] Failed to load thread messages:', err);
      if (!cancelled) {
        setInitialMessages(undefined);
        setResumeTurn(null);
        setReady(true);
      }
    });
    return () => { cancelled = true; };
  }, [effectiveThreadId, authLoading, user]);

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AiChatRuntimeInner ephemeral={ephemeral} initialMessages={initialMessages} resumeTurn={resumeTurn}>
      {children}
    </AiChatRuntimeInner>
  );
}

// ── Stable transport proxy (replaces useDynamicChatTransport from react-ai-sdk) ──

function useDynamicTransport(transport: InstanceType<typeof AssistantChatTransport>) {
  const ref = useRef(transport);
  useEffect(() => { ref.current = transport; });
  return useMemo(
    () =>
      new Proxy(ref.current, {
        get(_, prop) {
          const val = (ref.current as any)[prop];
          return typeof val === 'function' ? val.bind(ref.current) : val;
        },
      }),
    [],
  );
}

// ── Runtime ─────────────────────────────────────────────────────────────────
// Only mounts AFTER messages are loaded, so useChat gets the correct
// messages on its very first call (when the internal Chat object is created).

interface AiChatRuntimeInnerProps {
  children: ReactNode;
  ephemeral?: boolean;
  initialMessages?: UIMessage[];
  /** A turn still running on the thread, to reattach to on mount. */
  resumeTurn?: ActiveTurn | null;
}

function AiChatRuntimeInner({ children, ephemeral, initialMessages, resumeTurn }: AiChatRuntimeInnerProps) {
  const { user } = useAuth();
  const { currentThreadId, setCurrentThreadIdDirect, refreshThreads } = useThreadState();
  const { sectionContext } = useSectionContext();
  const { selectedModel, selectedEffort } = useModelSelection();

  const effectiveThreadId = ephemeral ? null : currentThreadId;

  const sectionContextRef = useRef(sectionContext);
  sectionContextRef.current = sectionContext;

  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;
  const selectedEffortRef = useRef(selectedEffort);
  selectedEffortRef.current = selectedEffort;

  const threadIdRef = useRef(effectiveThreadId);
  // The message version the running turn's history came from, captured when it
  // is sent. Its end-of-stream save carries this rather than whatever version is
  // current by then: a revert in between must make that save stale, not bless it.
  const turnBaseVersionRef = useRef<number | undefined>(undefined);
  // Sync from state only when effectiveThreadId actually changes.
  // Do NOT overwrite on every render — the fetch callback sets this ref
  // before the parent state update propagates, and intermediate re-renders
  // (from useChat stream updates) would reset it to null, causing the
  // backend to create duplicate threads.
  useLayoutEffect(() => {
    threadIdRef.current = effectiveThreadId;
  }, [effectiveThreadId]);

  // The turn this chat is streaming: made at send time, or the one reattached
  // to. What Stop names.
  const currentTurnIdRef = useRef<string | null>(resumeTurn?.turnId ?? null);
  // The reattached stream's request, which Stop ends.
  const reattachAbortRef = useRef<AbortController | null>(null);

  const callbacksRef = useRef({ setCurrentThreadIdDirect, refreshThreads });
  callbacksRef.current = { setCurrentThreadIdDirect, refreshThreads };

  // Background tasks (BackgroundTasks.tsx): the thread's tasks and running
  // turn, pushed as they change — how this chat learns that a finished job
  // started a turn here.
  const taskStore = useMemo(createTaskEventsStore, []);
  const getTaskToken = useCallback(async (): Promise<string | null> => {
    if (user && 'getIdToken' in user && typeof user.getIdToken === 'function') {
      try { return await user.getIdToken(); } catch { return null; }
    }
    return null;
  }, [user]);
  useTaskEventsConnection(taskStore, ephemeral ? null : effectiveThreadId, getTaskToken);

  const attachmentAdapter = useMemo(() => {
    const getToken = async (): Promise<string | null> => {
      if (user && 'getIdToken' in user && typeof user.getIdToken === 'function') {
        try { return await user.getIdToken(); } catch { return null; }
      }
      return null;
    };
    return new StorageAttachmentAdapter(getToken);
  }, [user]);

  const streamApiUrl = import.meta.env.VITE_API_STREAM_URL;

  const transport = useMemo(
    () =>
      new AssistantChatTransport({
        api: streamApiUrl ? `${streamApiUrl}/api/chat` : '/api/chat',
        credentials: streamApiUrl ? 'include' : 'same-origin',
        // Reattaching to a running turn (a reload mid-turn) reads its stream
        // from the start; the server replays what was missed, then follows.
        prepareReconnectToStreamRequest: ({ headers, credentials }) => ({
          api: `${streamApiUrl ?? ''}/api/chat/threads/${threadIdRef.current}/stream?after=0`,
          headers,
          credentials,
        }),
        fetch: async (input, init) => {
          // Only the POST starts a turn; the GET that reattaches to one is
          // passed straight through.
          // The AI SDK gives the reattach no abort signal, so Stop could not end
          // it; it gets one here, which `stopTurn` aborts.
          if ((init?.method ?? 'GET').toUpperCase() !== 'POST') {
            const controller = new AbortController();
            reattachAbortRef.current = controller;
            return globalThis.fetch(input, { ...init, signal: controller.signal });
          }
          // The turn outlives this request: a dropped connection, a switched
          // thread or a closed tab leaves it running, and a reload reattaches.
          // Only an explicit Stop ends it — sent from `chat.stop` below, naming
          // the turn by an id made here, at send time, so a Stop pressed
          // before the server set the turn up, or before a new thread even had
          // an id, still finds it.
          const turnId = crypto.randomUUID();
          currentTurnIdRef.current = turnId;
          if (typeof init?.body === 'string') {
            try {
              init = { ...init, body: JSON.stringify({ ...JSON.parse(init.body), turnId }) };
            } catch {
              /* not JSON — sent as-is, and Stop falls back to the thread id */
            }
          }
          turnBaseVersionRef.current = threadIdRef.current
            ? knownMessagesVersion(threadIdRef.current)
            : 0;
          const response = await globalThis.fetch(input, init);
          if (!ephemeral) {
            const backendThreadId = response.headers.get('X-Thread-Id');
            const wasCreated = response.headers.get('X-Thread-Created') === '1';
            if (backendThreadId && (!threadIdRef.current || wasCreated)) {
              threadIdRef.current = backendThreadId;
              if (wasCreated) noteMessagesVersion(backendThreadId, 0);
              callbacksRef.current.setCurrentThreadIdDirect(backendThreadId);
              if (wasCreated) {
                callbacksRef.current.refreshThreads();
              }
            }
          }
          return response;
        },
        headers: async () => {
          const headers: Record<string, string> = {};
          if (
            user &&
            'getIdToken' in user &&
            typeof user.getIdToken === 'function'
          ) {
            try {
              const token = await user.getIdToken();
              headers['Authorization'] = `Bearer ${token}`;
            } catch {
              /* dev mode — no token */
            }
          }
          return headers;
        },
        body: async () => {
          const extra: Record<string, unknown> = {
            modelId: selectedModelRef.current,
            effort: selectedEffortRef.current,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
          if (threadIdRef.current) {
            extra.threadId = threadIdRef.current;
          }
          if (sectionContextRef.current) {
            extra.sectionContext = sectionContextRef.current;
          }
          return extra;
        },
      }),
    [user, streamApiUrl, ephemeral],
  );

  const handleFinish = useCallback(
    async ({ messages, isError }: { messages: UIMessage[]; isError?: boolean }) => {
      if (ephemeral || isError || messages.length === 0) return;

      const tid = threadIdRef.current;
      if (!tid) return;

      const title = deriveTitleFromMessages(messages);
      const base = turnBaseVersionRef.current;
      const update = { messages, title, ...(base !== undefined ? { messagesVersion: base } : {}) };
      try {
        await threadApi.update(tid, update);
        callbacksRef.current.refreshThreads();
      } catch (err) {
        // The thread was reverted after this turn's history was loaded, so these
        // messages include what the revert removed. Dropping them is the point;
        // retrying would put the reverted messages back.
        if (err instanceof ApiRequestError && err.code === 'STALE_MESSAGES') {
          console.info('[AiChat] Not saving messages from before a revert:', err.message);
          return;
        }
        console.warn('[AiChat] Failed to persist messages:', err);
        setTimeout(() => {
          threadApi.update(tid, update).then(() => {
            callbacksRef.current.refreshThreads();
          }).catch((retryErr) => {
            console.error('[AiChat] Retry persist failed:', retryErr);
          });
        }, 2000);
      }
    },
    [ephemeral],
  );

  // Stop the turn on the server — the explicit Stop, never a dropped stream.
  const stopTurn = useCallback(() => {
    const turnId = currentTurnIdRef.current;
    if (turnId) void requestTurnStop(turnId, ephemeral ? null : threadIdRef.current);
    reattachAbortRef.current?.abort();
    reattachAbortRef.current = null;
  }, [ephemeral]);

  const runtime = useCustomChatRuntime({
    transport,
    initialMessages,
    onFinish: handleFinish,
    attachmentAdapter,
    stopTurn,
    resumeOnMount: !!resumeTurn,
    taskStore,
    currentTurnIdRef,
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <TaskEventsContext.Provider value={taskStore}>
        {children}
      </TaskEventsContext.Provider>
    </AssistantRuntimeProvider>
  );
}

// ── Custom Chat Runtime Hook ─────────────────────────────────────────────────

interface CustomChatRuntimeOpts {
  transport: InstanceType<typeof AssistantChatTransport>;
  initialMessages?: UIMessage[];
  onFinish: (opts: { messages: UIMessage[]; isError?: boolean; isAbort?: boolean }) => void;
  attachmentAdapter: any;
  /** Tell the server to stop the current turn. */
  stopTurn: () => void;
  /** Reattach to the thread's running turn once mounted. */
  resumeOnMount: boolean;
  /** The thread's tasks and running turn, as the server pushes them. */
  taskStore?: TaskEventsStore;
  /** The turn this chat is streaming (made at send time, or reattached to). */
  currentTurnIdRef?: { current: string | null };
}

function useCustomChatThreadRuntime({
  transport: rawTransport,
  initialMessages,
  onFinish,
  attachmentAdapter,
  stopTurn,
  resumeOnMount,
  taskStore,
  currentTurnIdRef,
}: CustomChatRuntimeOpts) {
  const dynamicTransport = useDynamicTransport(rawTransport);
  const threadId = useAuiState((s: any) => s.threadListItem.id);

  // A stopped turn never receives the server's closing timing (the connection
  // is gone before the `abort` metadata chunk), so stamp the response as
  // stopped here — in the live state, for "You stopped after …", and in what
  // handleFinish saves, so this save can't drop the stop the server records.
  const setMessagesRef = useRef<((messages: UIMessage[]) => void) | null>(null);
  const chat = useChat({
    id: threadId,
    transport: dynamicTransport as any,
    ...(initialMessages ? { messages: initialMessages } : {}),
    // A client-side tool (ask_user) ends the turn on its call; once the user
    // answers, send the answer back so the agent carries on.
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
    onFinish: (opts) => {
      if (!opts.isAbort) {
        onFinish(opts);
        return;
      }
      const messages = stampAbortedTurn(opts.messages);
      if (messages !== opts.messages) setMessagesRef.current?.(messages);
      onFinish({ ...opts, messages });
    },
  });
  setMessagesRef.current = chat.setMessages;

  // Reattach once, on mount, to a turn the thread reports as running.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (!resumeOnMount || resumedRef.current) return;
    resumedRef.current = true;
    void chat.resumeStream();
  }, [resumeOnMount, chat]);

  // Reattach to a turn that starts while this chat is idle and that this chat
  // did not send: a wake turn (a background task ended), or a turn sent from
  // another tab. Its prompt goes in first, as on a reload mid-turn, so the
  // reply is not read as a continuation of the previous answer — which is why
  // this waits until the turn's record carries it.
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const joinedTurnsRef = useRef(new Set<string>());
  const wakeTurnRef = useRef<string | null>(null);
  useEffect(() => {
    if (!taskStore || !currentTurnIdRef) return;
    const check = () => {
      const turn = taskStore.get().activeTurn;
      const c = chatRef.current;
      if (!turn?.turnId || turn.state !== 'running') return;
      if (turn.turnId === currentTurnIdRef.current || joinedTurnsRef.current.has(turn.turnId)) return;
      if (c.status !== 'ready' && c.status !== 'error') return;
      const prompt = turn.userMessage as UIMessage | undefined;
      if (!prompt?.id) return;
      joinedTurnsRef.current.add(turn.turnId);
      currentTurnIdRef.current = turn.turnId;
      if (turn.wake || (prompt as any).metadata?.taskNotification) wakeTurnRef.current = turn.turnId;
      if (!c.messages.some((m) => m.id === prompt.id)) c.setMessages([...c.messages, prompt]);
      void c.resumeStream();
    };
    check();
    return taskStore.subscribe(check);
  }, [taskStore, currentTurnIdRef, chat.status]);

  // A woken turn that finished while the tab was in the background says so.
  const prevStatusRef = useRef(chat.status);
  useEffect(() => {
    const was = prevStatusRef.current;
    prevStatusRef.current = chat.status;
    if (!wakeTurnRef.current) return;
    if ((was === 'streaming' || was === 'submitted') && (chat.status === 'ready' || chat.status === 'error')) {
      wakeTurnRef.current = null;
      notifyWakeTurnDone('The agent picked up a finished task', 'A background task ended and the agent has replied.');
    }
  }, [chat.status]);

  // Stopping in the UI (Stop, or an edit/retry that replaces the turn) is the
  // one thing that stops the turn on the server. Wrapped here because a
  // reattached stream has no request of ours to abort.
  const { stop } = chat;
  const status = chat.status;
  const stoppableChat = useMemo(
    () => ({
      ...chat,
      stop: async () => {
        if (status === 'streaming' || status === 'submitted') stopTurn();
        await stop();
      },
    }),
    [chat, stop, status, stopTurn],
  );

  const runtime = useAISDKRuntime(stoppableChat, {
    adapters: {
      attachments: attachmentAdapter,
    },
  });

  if (rawTransport instanceof AssistantChatTransport) {
    (rawTransport as any).setRuntime?.(runtime);
  }

  return runtime;
}

function useCustomChatRuntime(opts: CustomChatRuntimeOpts) {
  const cloudAdapter = unstable_useCloudThreadListAdapter({});
  return unstable_useRemoteThreadListRuntime({
    runtimeHook: function RuntimeHook() {
      return useCustomChatThreadRuntime(opts);
    },
    adapter: cloudAdapter,
    allowNesting: true,
  });
}

// ── Public wrapper ──────────────────────────────────────────────────────────

interface AiChatRuntimeProps {
  children: ReactNode;
  ephemeral?: boolean;
}

/**
 * Wrap your <AiChat /> with this. It auto-keys itself from chatSessionKey
 * so thread switches give a fresh runtime without remounting the rest of the page.
 *
 * Pass `ephemeral` for the slide-over panel to skip all persistence.
 */
export function AiChatRuntime({ children, ephemeral }: AiChatRuntimeProps) {
  const { chatSessionKey } = useThreadState();

  return (
    <AiChatErrorBoundary key={chatSessionKey}>
      <AiChatThreadLoader key={chatSessionKey} ephemeral={ephemeral}>
        {children}
      </AiChatThreadLoader>
    </AiChatErrorBoundary>
  );
}
